import React, { useEffect, useRef, useState } from 'react';
import { Scrollbars } from 'rc-scrollbars';
import { inject, observer } from 'mobx-react';
import { Icon, Tooltip } from '@/components';
import s from './index.less';
import LanguageSelect from '../BarragePanel/components/LanguageSelect';
import { transcribe } from '@/models';
import HoverIcon from '../BarragePanel/components/HoverIcon';
import RecordList from '@/components/RecordList';
import { useBarrageList } from '../../hooks/useBarrage/use-barrage-list';
import { useMemoizedFn } from 'ahooks';
import { useTranscribe } from '../../hooks/useBarrage/use-transcribe';
import { useLanguage } from '../../hooks/useBarrage/use-language';
import { TranscribeStatus } from '../ControlBar/PC/TranscribeBtn';
import cls from 'classnames';
import { useIntl } from 'umi';
import { Lang } from '@/types/lang';
interface TranscribePanelProps {
  visible: boolean;
  meeting?: any;
}

const TranscribePanel: React.FC<TranscribePanelProps> = inject(({ store }) => {
  return {
    meeting: store.meeting,
    user: store.user,
    chat: store.chat,
    rtc: store.rtc,
    transcribe: store.transcribe,
  };
})(
  observer((props: TranscribePanelProps) => {
    const { visible, meeting } = props;
    const scrollDom = React.useRef<Scrollbars>(null);
    const { barrageList, hasMore, setHasMore, loadHistoryRecord, pushRecord, clearRecord } =
      useBarrageList();
    const { resMsg, connect } = useTranscribe();
    const { formatMessage } = useIntl();
    const onLanguageChange = (data: { originLang: Lang; targetLang: Lang }) => {
      transcribe?.setOriginLang(data.originLang, true);
      transcribe?.setTargetLang(data.targetLang, true);
    };
    const { originLang, targetLang, enableSwitchLanguage } = useLanguage();

    const stompMsgToTranscribeMsg = (stmopMsg: any) => {
      const member = meeting?.membersMap.get(`${stmopMsg.sentences?.[0]?.speakerYxUid}`);
      return {
        timestamp: stmopMsg.timestamp,
        subtitleContent: stmopMsg.sentences?.[0]?.sentence || '',
        id: stmopMsg.sentences?.[0]?.speakerYxUid + stmopMsg?.timestamp?.toString(),
        name: member?.memberName,
        url: '',
        yxuid: stmopMsg.sentences?.[0]?.speakerYxUid,
        sentenceType: stmopMsg.sentences?.[0]?.sentenceType,
        uid: stmopMsg.sentences?.[0]?.speakerMemberUid,
      };
    };

    const renderThumb = ({ style, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
      const thumbStyle = {
        backgroundColor: `rgba(255, 255, 255, 0.5)`,
        borderRadius: '3px',
        width: '6px',
      };
      return <div style={{ ...style, ...thumbStyle }} {...props} />;
    };

    const fetchListIsLoading = useRef(false);
    const fetchList = useMemoizedFn(() => {
      if (fetchListIsLoading.current) return;
      fetchListIsLoading.current = true;
      return API.api.webSubtitleV3.queryMsgByTimestamp
        .request({
          channelId: meeting?.info?.channelId,
          timestamp: barrageList?.[0]?.timestamp || new Date().getTime(),
          size: 50,
        })
        .then((res) => {
          if (res.status === 1 && res.data) {
            const { hasMore, data } = res.data;
            setHasMore(!!hasMore);
            // loadHistoryRecord(data?.sort((a, b) => a.timestamp - b.timestamp));
            loadHistoryRecord(
              data?.map((n) => {
                return {
                  yxuid: n.speakerYxMeetingUid,
                  ...n,
                };
              })
            );
          }
        })
        .finally(() => {
          fetchListIsLoading.current = false;
        });
    });

    const onScroll = () => {};

    // mock 的list数据
    // const fetchList = useCallback(() => {
    //   return getRecordList(50).then((ar) => {
    //     loadHistoryRecord(
    //       ar.list.map((n, index) => {
    //         return {
    //           ...n,
    //           name: '你好',
    //           timestamp: new Date().getTime() + 10000 * index,
    //         };
    //       })
    //     );
    //   });
    // }, []);

    const handleCloseTranscribe = () => {
      transcribe.setTranscribeVisible(!transcribe.transcribeVisible);
    };
    useEffect(() => {
      if (resMsg && transcribe.transcribeStatus === TranscribeStatus.intranscribe) {
        const msg = stompMsgToTranscribeMsg(resMsg);
        pushRecord(msg);
      }
    }, [resMsg, transcribe.transcribeStatus]);

    useEffect(() => {
      if (enableSwitchLanguage) {
        transcribe?.setOriginLang(originLang, true);
        transcribe?.setTargetLang(targetLang, true);
      } else {
        transcribe?.setOriginLang(originLang, false);
        transcribe?.setTargetLang(targetLang, false);
      }
    }, [originLang, targetLang, enableSwitchLanguage]);

    useEffect(() => {
      if (transcribe?.transcribeVisible) {
        connect(true);
      } else {
        clearRecord(true);
      }
    }, [transcribe?.transcribeVisible]);

    if (!visible) {
      return null;
    }
    return (
      <div className={s.panel}>
        <div className={s.title}>
          <span className={s.left}>
            <span className={s.transcribe}>{formatMessage({ id: 'transcribe' })}</span>
            <Tooltip
              align={{ offset: [0, 10] }}
              title={formatMessage({ id: 'transcriptionHelpTip' })}
              placement="bottom"
              className={s.tooltip}
            >
              <Icon name="icon-question_1" mode="symbol" size={16} className={s.iconqus}></Icon>
            </Tooltip>
          </span>

          <div className={s.right}>
            {enableSwitchLanguage && (
              <span className={s.language}>
                <LanguageSelect
                  onChange={onLanguageChange}
                  originLang={transcribe?.originLang}
                  targetLang={transcribe?.targetLang}
                ></LanguageSelect>
              </span>
            )}

            <HoverIcon
              title={formatMessage({ id: 'CLOSE' })}
              name="icon-close_1"
              onClick={handleCloseTranscribe}
            ></HoverIcon>
          </div>
        </div>

        {transcribe.transcribeStatus === TranscribeStatus.pauseTranscribe && (
          <span className={s.stop}>{formatMessage({ id: 'transcribePaused' })}</span>
        )}

        <div
          className={cls(
            s.list,
            transcribe.transcribeStatus === TranscribeStatus.pauseTranscribe ? s.listTop : ''
          )}
        >
          <Scrollbars renderThumbVertical={renderThumb} ref={scrollDom} onScrollFrame={onScroll}>
            <RecordList
              list={barrageList}
              isWebTranscribe
              loadMore={fetchList}
              hasMore={hasMore}
              meetingInfo={meeting.meetingInfo}
            />
          </Scrollbars>
        </div>
      </div>
    );
  })
);

export default TranscribePanel;
